import { useEffect, useState } from "react";
import { Container, Row, Col, Breadcrumb } from "react-bootstrap";
import UsersList from "../components/UsersList";
import ModalForm from "../components/ModalForm";
import ClinicPatientForm from "../components/ClinicPatientForm";
import SubmitButton from "../components/SubmitButton";
import Button from "../components/Button";
import styles from "./ClinicPatients.module.scss";
import Loader from "../components/Loader";
import { alertModal } from "../utils/modalController";
import { Link, useNavigate } from "react-router-dom";
import { fetchAuth } from "../utils/fetchAuth";

const apiUrl = process.env.REACT_APP_API_URL || '';

export default function ClinicPatients() {
    const [users, setUsers] = useState<User[]>([]);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState<string>("");
    const [showModal, setShowModal] = useState(false);
    const [modalTitle, setModalTitle] = useState("Novo paciente");
    const [saving, setSaving] = useState(false);

    const navigate = useNavigate();

    const handleUserClick = (user: User) => {
        setSelectedUser(user);
        navigate(`/consultorio/pacientes/${user.id}`);
        //setModalTitle("Editar paciente");
        //setShowModal(true);
    };

    const handleAddUserClick = () => {
        setModalTitle("Novo paciente");
        setShowModal(true);
        setSelectedUser(null);
    };

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await fetchAuth(`${apiUrl}/consultorio/patients?query=${query}`, {
                    method: "GET",
                });

                if (!response.ok) {
                    const error = await response.json();
                    throw new Error('Erro ao buscar pacientes: ' + error.message);
                }

                const response_json = await response.json();

                setUsers(response_json.data); // ou response_json, se não estiver aninhado
            } catch (error) {
                console.error("Erro:", error);
                alertModal("Erro ao buscar usuários: " + error);
            } finally {
                setLoading(false);
            }
        };

        fetchUsers();
    }, [query]); // Adicione showModal como dependência para atualizar a lista de usuários quando o modal for fechado


    return (
        <>
            <Container className={styles.container}>
                <h2 className={styles.title}>Consultório</h2>
                <Row className={styles["row-breadcrumb"]}>
                    <Col>
                        <Breadcrumb className={styles.breadcrumb}>
                            <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/consultorio" }}>Consultório</Breadcrumb.Item>
                            <Breadcrumb.Item active>Pacientes</Breadcrumb.Item>
                        </Breadcrumb>
                    </Col>
                </Row>
                <Row className={styles["row-pacientes"]}>
                    <Col>
                        {loading ? (
                            <Loader />
                        ) : (
                            <UsersList
                                onUserClick={handleUserClick}
                                onAddUserClick={handleAddUserClick}
                                users={users}
                                mode={'paciente'}
                                onQueryChange={(query) => {
                                    setQuery(query);
                                }}
                            />
                        )}
                    </Col>
                </Row>
            </Container>
            <ModalForm
                title={modalTitle}
                show={showModal}
                onHide={() => { setShowModal(false) }}
                onSubmit={(e) => {
                    e.preventDefault();
                    setSaving(true);
                    const formData = new FormData(e.currentTarget);
                    const formDataJson = Object.fromEntries(formData.entries());
                    fetchAuth(apiUrl + "/consultorio/patients", {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(formDataJson),
                    })
                        .then(async (response) => {
                            if (!response.ok) {
                                const error_json = await response.json();
                                throw new Error(error_json.message || "Erro desconhecido");
                            }
                            return response.json();
                        })
                        .then((response_json) => {
                            setShowModal(false);
                            setUsers((prevUsers) => {
                                const updatedUsers = prevUsers.filter(user => user.id !== response_json?.patient?.id);
                                updatedUsers.push({
                                    id: response_json?.patient?.id,
                                    name: response_json?.name,
                                    email: response_json?.email,
                                    modified: response_json?.patient?.modified,
                                });
                                return updatedUsers;
                            });
                            setSelectedUser(null);
                        })
                        .catch((error) => {
                            console.error("Erro ao salvar paciente:", error);
                            alertModal("Erro ao salvar paciente: " + error.message);
                        })
                        .finally(() => {
                            setSaving(false);
                        });
                }}
                footerButtons={[
                    <Button
                        label="Cancelar"
                        className={styles['buttons']}
                        onClick={() => setShowModal(false)}
                        variant="secondary"
                        disabled={saving}
                    />,
                    <SubmitButton
                        className={styles['buttons']}
                        disabled={saving}
                    >
                        {saving ? "Salvando..." : "Salvar"}
                    </SubmitButton>,
                ]}
            >
                <ClinicPatientForm patient={selectedUser ?? undefined} />
            </ModalForm>
        </>
    );
}
